import {AppNumberPipe, formatAppNumber} from '@frontend/shared';
import {Component, computed, inject, input, signal} from '@angular/core';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {UIChart} from 'primeng/chart';
import {ChartData, ChartOptions, Plugin, TooltipItem} from 'chart.js';
import {visibleShares} from '../../company-info.calc';
import {DonutLegendEntry, DonutSegment, DonutValueMode} from './workforce-donut-chart.types';

function resolveCssColor(variableName: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
}

const HOVER_OFFSET = 6;

type NumberFormatter = (value: number) => string;

function createCenterTotalPlugin(formatTotal: NumberFormatter): Plugin<'doughnut'> {
  return {
    id: 'centerTotal',
    afterDraw: (chart) => {
      const {ctx, chartArea} = chart;
      if (!chartArea) {
        return;
      }
      const data = chart.data.datasets[0]?.data ?? [];
      const total = data.reduce<number>(
        (sum, value) => sum + (typeof value === 'number' ? value : 0),
        0,
      );
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = resolveCssColor('--p-text-color');
      ctx.font = 'bold 20px sans-serif';
      ctx.fillText(
        formatTotal(total),
        (chartArea.left + chartArea.right) / 2,
        (chartArea.top + chartArea.bottom) / 2,
      );
      ctx.restore();
    },
  };
}

function buildChartOptions(
  percent: boolean,
  formatTotal: NumberFormatter,
  formatShare: NumberFormatter,
): ChartOptions<'doughnut'> {
  return {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    cutout: '70%',
    layout: {padding: HOVER_OFFSET},
    plugins: {
      legend: {display: false},
      datalabels: {display: false},
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'doughnut'>) => {
            const value = context.parsed;
            const total = context.dataset.data.reduce<number>(
              (sum, entry) => sum + (typeof entry === 'number' ? entry : 0),
              0,
            );
            if (percent) {
              const share = total > 0 ? (value / total) * 100 : 0;
              return `${context.label}: ${formatShare(share)} %`;
            }
            return `${context.label}: ${formatTotal(value)}`;
          },
        },
      },
    },
  };
}

@Component({
  selector: 'app-workforce-donut-chart',
  imports: [TranslocoPipe, AppNumberPipe, UIChart],
  templateUrl: './workforce-donut-chart.component.html',
  styleUrl: './workforce-donut-chart.component.scss',
})
export class WorkforceDonutChartComponent {
  readonly titleKey = input.required<string>();
  readonly segments = input.required<DonutSegment[]>();
  readonly mode = input<DonutValueMode>('absolute');
  readonly digitsInfo = input('1.0-0');
  readonly testId = input.required<string>();

  private readonly transloco = inject(TranslocoService);
  private readonly formatTotal: NumberFormatter = (value) =>
    formatAppNumber(value, this.transloco.getActiveLang(), '1.0-2');

  protected readonly chartPlugins = [createCenterTotalPlugin(this.formatTotal)];

  private readonly hiddenKeys = signal<ReadonlySet<string>>(new Set());

  private readonly shares = computed(() => visibleShares(this.segments(), this.hiddenKeys()));
  protected readonly isPercent = computed(() => this.mode() === 'percent');
  protected readonly legend = computed<DonutLegendEntry[]>(() => {
    const hiddenKeys = this.hiddenKeys();
    const shareByKey = this.shares().shareByKey;
    return this.segments().map((segment) => ({
      ...segment,
      hidden: hiddenKeys.has(segment.key),
      share: shareByKey.get(segment.key) ?? 0,
    }));
  });
  protected readonly chartData = computed<ChartData<'doughnut'>>(() => {
    const visible = this.shares().visible;
    return {
      labels: visible.map((segment) => segment.label),
      datasets: [
        {
          data: visible.map((segment) => segment.value),
          backgroundColor: visible.map((segment) => resolveCssColor(segment.colorVar)),
          borderWidth: 0,
          hoverOffset: HOVER_OFFSET,
        },
      ],
    };
  });
  protected readonly chartOptions = computed(() =>
    buildChartOptions(this.isPercent(), this.formatTotal, (share) =>
      formatAppNumber(share, this.transloco.getActiveLang(), '1.1-1'),
    ),
  );

  toggleSegment(key: string): void {
    this.hiddenKeys.update((hiddenKeys) => {
      const next = new Set(hiddenKeys);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }
}
